import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, Trash2, Loader2, KeyRound, ShieldCheck, CheckCircle2, XCircle, Users as UsersIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", role: "viewer", status: "active" };
const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "analyst", label: "Analyst" },
  { value: "viewer", label: "Viewer" },
];

const ROLE_BADGE = {
  admin: "bg-[#0A3D91]/10 text-[#0A3D91]",
  manager: "bg-[#0E73C9]/10 text-[#0E73C9]",
  analyst: "bg-[#18B7D8]/15 text-[#0E73C9]",
  viewer: "bg-slate-100 text-slate-600",
};

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwReset, setPwReset] = useState(null); // { user, newPw }

  const isAdmin = me?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setList(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="card-harmony p-12 text-center">
        <ShieldCheck className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h2 className="font-display text-xl font-bold text-slate-900">Admin access required</h2>
        <p className="text-sm text-slate-500 mt-2">
          Only administrators can manage HARMONY users.
        </p>
      </div>
    );
  }

  const onOpenNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const onOpenEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, status: u.status || "active" });
    setOpen(true);
  };

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Invalid email format";
    if (!ROLES.some((r) => r.value === form.role)) return "Invalid role";
    if (!editing && (!form.password || form.password.length < 6)) return "Password must be at least 6 characters";
    if (editing && form.password && form.password.length < 6) return "New password must be at least 6 characters";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    try {
      if (editing) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          role: form.role,
          status: form.status,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", {
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          role: form.role,
          status: form.status,
        });
        toast.success("User created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/users/${confirmDel.id}`);
      toast.success("User deleted");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onPwReset = async () => {
    if (!pwReset?.newPw || pwReset.newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.put(`/users/${pwReset.user.id}`, { password: pwReset.newPw });
      toast.success(`Password reset for ${pwReset.user.email}`);
      setPwReset(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "disabled" ? "active" : "disabled";
    try {
      await api.put(`/users/${u.id}`, { status: next });
      toast.success(`User ${next === "active" ? "activated" : "disabled"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">User Management</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage HARMONY users, roles and access. Roles: Admin · Manager · Analyst · Viewer.
          </p>
        </div>
        <button onClick={onOpenNew} className="btn-primary" data-testid="new-user-btn">
          <Plus className="h-4 w-4" /> Add User
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Summary label="Total Users" value={list.length} accent="deep" icon={UsersIcon} />
        <Summary label="Admins" value={list.filter((u) => u.role === "admin").length} accent="blue" icon={ShieldCheck} />
        <Summary label="Managers" value={list.filter((u) => u.role === "manager").length} accent="cyan" icon={UsersIcon} />
        <Summary label="Active" value={list.filter((u) => (u.status || "active") === "active").length} accent="teal" icon={CheckCircle2} />
      </div>

      <div className="card-harmony overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">No users yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-left py-3 px-4">Created</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const status = u.status || "active";
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#0A3D91] to-[#1CC7B3] text-white flex items-center justify-center text-xs font-semibold">
                          {u.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{u.name}{isMe && <span className="ml-2 text-[10px] text-slate-400">(you)</span>}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${ROLE_BADGE[u.role] || ROLE_BADGE.viewer}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {status === "active" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        {status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => onOpenEdit(u)}
                        className="inline-flex items-center gap-1 text-xs text-[#0E73C9] hover:text-[#0A3D91] font-medium mr-3"
                        data-testid={`user-edit-${u.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => setPwReset({ user: u, newPw: "" })}
                        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#0A3D91] font-medium mr-3"
                        data-testid={`user-reset-pw-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5" /> Reset PW
                      </button>
                      {!isMe && (
                        <button
                          onClick={() => toggleStatus(u)}
                          className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium mr-3"
                        >
                          {status === "active" ? "Disable" : "Activate"}
                        </button>
                      )}
                      {!isMe && (
                        <button
                          onClick={() => setConfirmDel(u)}
                          className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 font-medium"
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Create User"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update user details. Leave password blank to keep current password." : "Create a new HARMONY user account."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-form-name"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-form-email"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {editing ? "New Password (optional)" : "Password *"}
              </label>
              <input
                type="password"
                value={form.password}
                autoComplete="new-password"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editing ? "Leave blank to keep current" : "Minimum 6 characters"}
                data-testid="user-form-password"
                className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Role *</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="user-form-submit">
                {editing ? "Update User" : "Create User"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwReset} onOpenChange={(o) => !o && setPwReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new password for <b>{pwReset?.user?.email}</b>. They'll need to use it on their next sign in.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">New Password</label>
            <input
              type="password"
              autoFocus
              value={pwReset?.newPw || ""}
              onChange={(e) => setPwReset({ ...pwReset, newPw: e.target.value })}
              placeholder="Minimum 6 characters"
              data-testid="pw-reset-input"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setPwReset(null)} className="btn-secondary">Cancel</button>
            <button onClick={onPwReset} className="btn-primary" data-testid="pw-reset-confirm">
              <KeyRound className="h-4 w-4" /> Reset Password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <DialogDescription>
              This will permanently remove <b>{confirmDel?.email}</b>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirmDel(null)} className="btn-secondary">Cancel</button>
            <button onClick={onDelete} className="btn-primary bg-rose-600 hover:bg-rose-700">Delete</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Summary({ label, value, accent, icon: Icon }) {
  const bg = {
    deep: "bg-[#0A3D91]/8 text-[#0A3D91]",
    blue: "bg-[#0E73C9]/10 text-[#0E73C9]",
    cyan: "bg-[#18B7D8]/12 text-[#0E73C9]",
    teal: "bg-[#1CC7B3]/15 text-[#0B8F80]",
  };
  return (
    <div className="card-harmony p-5">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="flex items-center justify-between mt-3">
        <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${bg[accent]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}
